import { z } from 'zod';

/**
 * Wire contract for the Trellus Chrome-extension host protocol (Clay SEP
 * preview v15). Everything crosses the document-`CustomEvent` boundary as a
 * JSON string in `detail`, so every inbound payload is parsed through one of
 * these schemas with `safeParse` — unknown or malformed events are dropped
 * (and logged at debug) by `host-client.ts`, never thrown, never trusted.
 *
 * Trellus's events are UNTRUSTED page messages: a nonce coordinates document
 * freshness but authenticates nothing. These schemas only decide "is this
 * shaped like an event we understand"; they never grant authority.
 *
 * Types are derived from the schemas (`z.infer`) — there are no parallel
 * hand-written interfaces.
 */

/** DOM event names. Names verified against the v15 extension bundle. */
export const TRELLUS_EVENTS = {
  capabilitiesRequest: 'TRELLUS_CLAY_SEP_HOST_CAPABILITIES_REQUEST',
  capabilities: 'TRELLUS_CLAY_SEP_HOST_CAPABILITIES',
  command: 'TRELLUS_CLAY_SEP_HOST_COMMAND',
  commandResult: 'TRELLUS_CLAY_SEP_HOST_COMMAND_RESULT',
  contextRequest: 'TRELLUS_CLAY_SEP_HOST_CONTEXT_REQUEST',
  context: 'TRELLUS_CLAY_SEP_HOST_CONTEXT',
  selection: 'TRELLUS_CLAY_SEP_HOST_SELECTION',
  selectionResult: 'TRELLUS_CLAY_SEP_HOST_SELECTION_RESULT',
  focusResult: 'TRELLUS_CLAY_SEP_HOST_FOCUS_RESULT',
  sidebarState: 'TRELLUS_CLAY_SEP_SIDEBAR_STATE',
  bulkDialResult: 'TRELLUS_CLAY_SEP_BULK_DIAL_RESULT',
  gongConnectionState: 'TRELLUS_CLAY_SEP_GONG_CONNECTION_STATE',
  uiConfigurationChanged: 'TRELLUS_CLAY_SEP_UI_CONFIGURATION_CHANGED',
  completionPolicyChanged: 'TRELLUS_CLAY_SEP_COMPLETION_POLICY_CHANGED',
  taskEvent: 'TRELLUS_CLAY_SEP_TASK_EVENT',
} as const;

/** Largest `detail` string the client will parse for ordinary notifications (matches the extension's own 64 KiB bound). */
export const TRELLUS_MAX_DETAIL_CHARS = 65_536;
/**
 * `HOST_CONTEXT` and `HOST_SELECTION_RESULT` echo up to 10,000 ids (~250 KB of JSON), so they get a larger
 * — still bounded — ceiling. Without it a legitimate full-size selection's acknowledgement would be dropped.
 */
export const TRELLUS_MAX_ID_LIST_DETAIL_CHARS = 1_000_000;
/** Protocol cap on one selection broadcast. */
export const TRELLUS_MAX_SELECTED_IDS = 10_000;

const NonNegInt = z.number().int().nonnegative();
const PositiveInt = z.number().int().min(1);

const TrellusSelectionKindSchema = z.enum(['signals', 'contacts']);
export type TrellusSelectionKind = z.infer<typeof TrellusSelectionKindSchema>;

const GongStateSchema = z.enum(['connected', 'connecting', 'disconnected']);

const CompletionAfterCallSchema = z.enum(['default_on', 'off']);

/** `completion.*` snapshot: capabilities carry the effective state; the changed event carries only the requested preference. */
const TrellusCompletionPolicySchema = z.object({
  revision: PositiveInt,
  after_call: CompletionAfterCallSchema,
  requested_after_call: CompletionAfterCallSchema.optional(),
  effective_after_call: CompletionAfterCallSchema.nullable().optional(),
  rep_auto_complete_disabled: z.boolean().nullable().optional(),
});
export type TrellusCompletionPolicy = z.infer<typeof TrellusCompletionPolicySchema>;

/** `HOST_CAPABILITIES` reply (v2). `available:false` means "extension present but not usable here". */
export const TrellusCapabilitiesSchema = z.object({
  version: z.literal(2),
  request_id: z.string().min(1),
  available: z.literal(true),
  nonce: z.string().min(1).max(128),
  context_revision: NonNegInt,
  scope: z.literal('document'),
  commands: z.array(z.string()),
  outbound_events: z.array(z.string()).default([]),
  /**
   * Queue sources the extension can load a dial list from (`signals`, `contacts`, and the PROPOSED `contacts_scoped`
   * for account/play-scoped contact lists, requested from Trellus but not yet shipped). A v15 build
   * omits it. Gate every scoped dial control on it (`hasTrellusSource`); the name is Terra's proposal.
   */
  sources: z.array(z.string()).default([]),
  components: z.record(z.string(), z.unknown()),
  build_id: z.string().optional(),
  extension_version: z.string().optional(),
  completion: TrellusCompletionPolicySchema.optional(),
  limits: z.object({
    command_timeout_ms: PositiveInt.optional(),
    is_test_barrier: z.boolean().optional(),
  }).optional(),
});
export type TrellusCapabilities = z.infer<typeof TrellusCapabilitiesSchema>;

const TrellusCapabilitiesUnavailableSchema = z.object({
  version: z.literal(2),
  request_id: z.string().min(1),
  available: z.literal(false),
  reason_code: z.string().optional(),
});

// ── Observed (scoped) sources ───────────────────────────────────────────────

/** One entry of `HOST_CONTEXT.observed_sources`. Everything is optional: the live capture carried only kind/source_url/available. */
export const TrellusObservedSourceSchema = z.object({
  kind: TrellusSelectionKindSchema.nullable().optional(),
  source_url: z.string().nullable().optional(),
  available: z.boolean().optional(),
  revision: NonNegInt.optional(),
  account_id: z.string().nullable().optional(),
  play_id: z.string().nullable().optional(),
  play_type: z.string().nullable().optional(),
  play_section: z.string().nullable().optional(),
});
export type TrellusObservedSource = z.infer<typeof TrellusObservedSourceSchema>;

/** An observed source whose `source_url` is known to be a string (what a publish echoes back). */
export type TrellusScopedSource = TrellusObservedSource & { source_url: string };


/** `HOST_CONTEXT` (v1): which native table the extension currently sees, and its freshness token. */
export const TrellusContextSchema = z.object({
  version: z.literal(1),
  request_id: z.string().optional(),
  nonce: z.string().min(1).max(128),
  revision: NonNegInt,
  kind: TrellusSelectionKindSchema.nullable(),
  source_url: z.string().nullable(),
  available: z.boolean(),
  /** Set on a refused scoped request (`source_not_observed`, `view_unavailable`); absent on a normal context. */
  reason_code: z.string().nullable().optional(),
  retryable: z.boolean().optional(),
  selected_ids: z.array(z.string()).default([]),
  /**
   * Every list the extension has OBSERVED on the page, active or not (a build that tracks more than one source). Parsed
   * entry by entry at this boundary: one malformed entry (or one without a string `source_url`) is dropped and never fails
   * the whole context. OMITTED (a v15 build, or the reply to a scoped request, which describes one list) is deliberately
   * distinct from an explicit `[]` ("nothing observed"): the store keeps the previous report only for an omission.
   */
  observed_sources: z.array(z.unknown()).transform(list => list.flatMap((raw) => {
    const parsed = TrellusObservedSourceSchema.safeParse(raw);
    if (!parsed.success) return [];
    const url = parsed.data.source_url;
    return typeof url === 'string' ? [{ ...parsed.data, source_url: url }] : [];
  })).optional(),
});
export type TrellusContext = z.infer<typeof TrellusContextSchema>;

/** Which scoped contact list a dial belongs to. Only `account` is matched on the wire today. */
export type TrellusDialScope = { kind: 'account'; accountId: string };

const CONTACTS_LIST_PATH = '/api/flow/contacts';

/**
 * Does this observed source carry this scope? The `account_id` query parameter of its `source_url` is REQUIRED and
 * authoritative (it is the list a publish will target). An entry's own `account_id` field is only a cross-check: when present
 * it must equal the parameter, and it never substitutes for a missing one. The URL is only READ here, never rebuilt or
 * re-encoded: callers echo the entry's exact string.
 */
export function sourceMatchesScope(source: TrellusObservedSource, scope: TrellusDialScope): boolean {
  if (source.kind !== undefined && source.kind !== 'contacts') return false;
  if (source.available === false) return false;
  const url = source.source_url;
  if (typeof url !== 'string') return false;
  let parsed: URL;
  try {
    parsed = new URL(url, 'http://terra.invalid');
  } catch {
    return false;
  }
  if (!parsed.pathname.endsWith(CONTACTS_LIST_PATH)) return false;
  const urlAccountId = parsed.searchParams.get('account_id');
  if (urlAccountId !== scope.accountId) return false;
  return source.account_id == null || source.account_id === urlAccountId;
}

/** The first observed source that carries `scope`, or null. */
export function findObservedScopedSource(context: Pick<TrellusContext, 'observed_sources'>, scope: TrellusDialScope): TrellusScopedSource | null {
  return (context.observed_sources ?? []).find((source) => sourceMatchesScope(source, scope)) ?? null;
}

/** `HOST_SELECTION_RESULT` (v1). Acceptance means the contract was applied — NOT that rows are dialable or a call started. */
export const TrellusSelectionResultSchema = z.object({
  version: z.literal(1),
  request_id: z.string().min(1),
  accepted: z.boolean(),
  reason_code: z.string().nullable().optional(),
  nonce: z.string().optional(),
  revision: NonNegInt.optional(),
  sequence: NonNegInt.optional(),
  kind: TrellusSelectionKindSchema.nullable().optional(),
  source_url: z.string().nullable().optional(),
  selected_ids: z.array(z.string()).optional(),
  focused_id: z.string().nullable().optional(),
});
export type TrellusSelectionResult = z.infer<typeof TrellusSelectionResultSchema>;

/** `HOST_FOCUS_RESULT` (v1). `focused:false` is the normal "no matching card in the queue" answer. */
export const TrellusFocusResultSchema = z.object({
  version: z.literal(1),
  request_id: z.string().min(1),
  focused: z.boolean(),
  reason_code: z.string().nullable().optional(),
});
export type TrellusFocusResult = z.infer<typeof TrellusFocusResultSchema>;

/** `HOST_COMMAND_RESULT` (v2): `pending`, then a terminal `applied` / `rejected`. */
export const TrellusCommandResultSchema = z.object({
  version: z.literal(2),
  request_id: z.string().min(1),
  command: z.string().min(1),
  status: z.enum(['pending', 'applied', 'rejected']),
  reason_code: z.string().nullable().optional(),
  retryable: z.boolean().optional(),
  payload: z.unknown().optional(),
});

/** `sidebar.*` terminal payload and the pushed `SIDEBAR_STATE` event share these fields. */
const SidebarStateFields = {
  open: z.boolean(),
  state_sequence: NonNegInt,
  gong_state: GongStateSchema,
};
export const TrellusSidebarPayloadSchema = z.object(SidebarStateFields);
export type TrellusSidebarPayload = z.infer<typeof TrellusSidebarPayloadSchema>;

export const TrellusSidebarStateEventSchema = z.object({
  version: z.literal(2),
  event_type: z.literal('sidebar.state_changed'),
  ...SidebarStateFields,
});
export type TrellusSidebarStateEvent = z.infer<typeof TrellusSidebarStateEventSchema>;

/** Public `reason_code`s of an explicit checked-set load. The wire field stays a free string so a newer extension's code never drops the event. */
export const TRELLUS_BULK_DIAL_REASON_CODES = [
  'mixed_selection',
  'dnc',
  'no_phone',
  'no_dialable_rows',
  'selection_missing_rows',
  'no_selection',
  'stale_selection',
  'dialer_not_ready',
  'queue_busy',
  'parallel_unavailable',
  'load_failed',
  'selection_or_load_failed',
] as const;

const TrellusBulkDialCountsSchema = z.object({
  selected_count: NonNegInt,
  dialable_count: NonNegInt,
  non_call_count: NonNegInt,
  dnc_count: NonNegInt,
  no_phone_count: NonNegInt,
  invalid_count: NonNegInt,
  unknown_count: NonNegInt,
});
export type TrellusBulkDialCounts = z.infer<typeof TrellusBulkDialCountsSchema>;

/** `BULK_DIAL_RESULT` (`selection.load_result`): the outcome of the rep pressing Trellus's bulk-dial control. Trellus owns validation — surface this verbatim, never pre-filter. */
export const TrellusBulkDialResultSchema = z.object({
  version: z.literal(2),
  event_type: z.literal('selection.load_result'),
  event_id: z.string().min(1).max(128),
  request_id: z.string().optional(),
  status: z.enum(['applied', 'rejected']),
  reason_code: z.string().nullable(),
  retryable: z.boolean().optional(),
  kind: TrellusSelectionKindSchema.nullable().optional(),
  mode: z.string().optional(),
  counts: TrellusBulkDialCountsSchema.optional(),
  queue_loaded: z.boolean().optional(),
  start_requested: z.boolean().optional(),
});
export type TrellusBulkDialResult = z.infer<typeof TrellusBulkDialResultSchema>;

/**
 * `GONG_CONNECTION_STATE`. The v15 docs do not publish this payload; the shape
 * is taken from the extension's `getGongConnectionState()` (state, ready,
 * canReconnect, message). `state` is an open string — v15 emits e.g.
 * `checking`, `sign_in_required`, `wrong_user`, not just the sidebar's three.
 */
export const TrellusGongConnectionStateSchema = z.object({
  state: z.string().min(1).max(64),
  ready: z.boolean(),
  canReconnect: z.boolean().optional(),
});
export type TrellusGongConnectionState = z.infer<typeof TrellusGongConnectionStateSchema>;

export const TrellusUiConfigurationChangedSchema = z.object({
  version: z.literal(2),
  event_type: z.literal('ui.configuration_changed'),
  scope: z.literal('document'),
  component_id: z.string().min(1),
  config_revision: PositiveInt,
  appearance: z.record(z.string(), z.unknown()),
});
export type TrellusUiConfigurationChanged = z.infer<typeof TrellusUiConfigurationChangedSchema>;

export const TrellusCompletionPolicyChangedSchema = z.object({
  version: z.literal(2),
  event_type: z.literal('completion.configuration_changed'),
  scope: z.literal('document'),
  after_call: CompletionAfterCallSchema,
  revision: PositiveInt,
});
export type TrellusCompletionPolicyChanged = z.infer<typeof TrellusCompletionPolicyChangedSchema>;

const TRELLUS_TASK_EVENT_TYPES = [
  'task_completed',
  'task_completion_uncertain',
  'task_completion_failed',
  'task_already_completed',
  'connection_state_changed',
  'dial_started',
  'call_connected',
  'call_ended',
  'disposition_saved',
  'flow_removed',
] as const;

const NullableString = z.string().nullable().optional();

/**
 * `TASK_EVENT` (v2 envelope, schema_version 1). A UI HINT: unsigned
 * page-best-effort data. The only backend use is the owner-only dialer-event
 * hint (`useTrellusCompletionSync`, gong reconcile U20), which asks the server
 * to re-check a row; the server confirms with Gong except for D8's
 * `flow_removed` on the rep's own enrollment (docs/flow/trellus-dialer.md).
 */
export const TrellusTaskEventSchema = z.object({
  version: z.literal(2),
  schema_version: z.literal(1),
  event_id: z.string().min(1).max(128),
  event_type: z.enum(TRELLUS_TASK_EVENT_TYPES),
  event_sequence: NonNegInt.optional(),
  occurred_at: NullableString,
  nonce: z.string().nullable(),
  revision: NonNegInt.nullable(),
  is_test: z.boolean(),
  view_as: z.boolean().nullable().optional(),
  clay_signal_id: NullableString,
  clay_contact_id: NullableString,
  clay_account_id: NullableString,
  gong_workspace_id: NullableString,
  gong_task_id: NullableString,
  gong_flow_instance_id: NullableString,
  confirmation_source: NullableString,
  /** v16 top-level field (clay-trellus-messaging-handoff): the step of Gong's pinned task; null for manual / one-off completions. */
  gong_step_number: z.number().int().nullable().optional(),
  payload: z.record(z.string(), z.unknown()),
});
export type TrellusTaskEvent = z.infer<typeof TrellusTaskEventSchema>;

/** The step the event's completion refers to (Gong's pinned task): a positive integer, else null. The ONE reader of `gong_step_number`. */
export function completedStepNumber(event: TrellusTaskEvent): number | null {
  const step = event.gong_step_number;
  return typeof step === 'number' && Number.isInteger(step) && step > 0 ? step : null;
}

/** The payload of a completion event; only `completion_status` is read, everything else passes through. */
const TaskCompletionPayloadSchema = z.object({ completion_status: z.string().optional() }).passthrough();

/** A real, non-test, non-View-As event whose payload says the task completed. Event type is deliberately not checked here. */
export function isConfirmedCompletion(event: TrellusTaskEvent): boolean {
  if (event.is_test || event.view_as === true) return false;
  const parsed = TaskCompletionPayloadSchema.safeParse(event.payload);
  return parsed.success && parsed.data.completion_status === 'completed';
}

/** A real `flow_removed` event (removals carry no completion_status). */
export function isConfirmedRemoval(event: TrellusTaskEvent): boolean {
  return event.event_type === 'flow_removed' && !event.is_test && event.view_as !== true;
}

/** Event types that can carry a completion (`completion_status`), as opposed to a removal or a hint. */
const COMPLETION_EVENT_TYPES: ReadonlySet<string> = new Set(['task_completed', 'task_already_completed']);

/**
 * The ONE "confirmed done" predicate: a confirmed completion of a completion-type
 * event, or a confirmed removal. The host store's tombstone and the completion-sync
 * hook's post both read it, so a row is hidden exactly when the server is told.
 */
export function isConfirmedDone(event: TrellusTaskEvent): boolean {
  return (COMPLETION_EVENT_TYPES.has(event.event_type) && isConfirmedCompletion(event)) || isConfirmedRemoval(event);
}

/** Which document events carry which schema; the client dispatches on the DOM event name, then parses with the matching schema. */
export const TRELLUS_INBOUND_SCHEMAS = {
  capabilities: z.union([TrellusCapabilitiesSchema, TrellusCapabilitiesUnavailableSchema]),
  commandResult: TrellusCommandResultSchema,
  context: TrellusContextSchema,
  selectionResult: TrellusSelectionResultSchema,
  focusResult: TrellusFocusResultSchema,
  sidebarState: TrellusSidebarStateEventSchema,
  bulkDialResult: TrellusBulkDialResultSchema,
  gongConnectionState: TrellusGongConnectionStateSchema,
  uiConfigurationChanged: TrellusUiConfigurationChangedSchema,
  completionPolicyChanged: TrellusCompletionPolicyChangedSchema,
  taskEvent: TrellusTaskEventSchema,
} as const;
